import {
  DataFetcher,
  PriorityConfig,
  useGrpcRequest,
} from '@aboutyou/data-fetcher';
import {CategoryIdentifier} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/category_identifier.types';
import {BannerService_GetBannerLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/banner/service.grpc.lazy';
import {
  GetBannerRequest_Placement,
  GetBannerRequest_PriorityOverride,
  GetBannerResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/banner/service.types';
import {PageTypes, useCurrentPageType} from '@aboutyou/router';
import {ContentPopperProps} from '@aboutyou/ui-components';
import {useFullSession} from '@aboutyou/user';
import React, {FC} from 'react';
import {useCategoryIdentifier} from '../../backend-services/CategoryStream/utils/useCategoryIdentifier';
import {useTargetGroupIdentifier} from '../../shared-deprecated/useTargetGroupIdentifier';
import {useProductIdFromSlug} from '../ProductUrl';
import {useReductionsStateRequestParameter} from '../ReductionsState/useReductionsStateRequestParameter';
import {PromotionBannerView} from './PromotionBannerView';
import {useHeaderHighlightParams} from './utils/useHeaderHighlightParams';

export interface PromotionBannerProps extends PriorityConfig {
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  onClick?: VoidFunction;
  className?: string;
  ['data-testid']?: string;
  // Override for surfaces like the offcanvas where the inferred pageType is wrong.
  pageType?: PageTypes;
  // Opt out of both header-highlight URL params (`disableHeaderHighlight` and
  // `headerHighlightOverride`). Set by pages whose HTML is edge-cached without
  // varying on these params (e.g. ADP): honoring them only on the client would
  // diverge from the cached SSR render and trip a hydration mismatch — a
  // structural one for the disable param, a content one for the override.
  disableOverrides?: boolean;
}

const placementForPageType = (
  pageType: PageTypes | undefined,
  categoryIdentifier: CategoryIdentifier['identifier'],
  productId: number | undefined,
): GetBannerRequest_Placement | null => {
  switch (pageType) {
    case 'wishlist':
      return {type: {$case: 'wishlist', wishlist: {}}};
    case 'basket':
      return {type: {$case: 'basket', basket: {}}};
    case 'brandShop':
    case 'brandLegacy':
      return {type: {$case: 'shop', shop: {}}};
    case 'home':
      return {type: {$case: 'spotlight', spotlight: {}}};
    case 'category':
    case 'categoryLegacy':
      return {
        type: {
          $case: 'categoryPage',
          categoryPage: {category: {identifier: categoryIdentifier}},
        },
      };
    case 'articleDetail':
      if (productId === undefined) return null;
      return {
        type: {
          $case: 'articleDetailPage',
          articleDetailPage: {productId},
        },
      };
    case 'orderSuccess':
      return {type: {$case: 'osp', osp: {}}};
    default:
      return null;
  }
};

const mockForPlacement = (
  placement: GetBannerRequest_Placement,
): Promise<GetBannerResponse> => {
  switch (placement.type?.$case) {
    case 'wishlist':
      return import('./mocks/wishlistBanner.mock').then(
        m => m.wishlistBannerMock,
      );
    case 'basket':
      return import('./mocks/basketBanner.mock').then(m => m.basketBannerMock);
    case 'categoryPage':
      return import('./mocks/categoryBanner.mock').then(
        m => m.categoryBannerMock,
      );
    case 'articleDetailPage':
      return import('./mocks/articleDetailBanner.mock').then(
        m => m.articleDetailBannerMock,
      );
    case 'shop':
      return import('./mocks/shopBanner.mock').then(m => m.shopBannerMock);
    case 'osp':
      return import('./mocks/ospBanner.mock').then(m => m.ospBannerMock);
    default:
      return Promise.resolve({});
  }
};

const useBannerRequest = (
  placement: GetBannerRequest_Placement,
  priorityOverride: GetBannerRequest_PriorityOverride | undefined,
) => {
  const session = useFullSession();
  const targetGroup = useTargetGroupIdentifier();
  const reductionsState = useReductionsStateRequestParameter();
  return useGrpcRequest(
    BannerService_GetBannerLazy,
    {
      session: session || null,
      targetGroup,
      placement,
      priorityOverride: priorityOverride ?? null,
      reductionsState,
    },
    {
      mockData: () => mockForPlacement(placement),
    },
  );
};

export const PromotionBanner: FC<PromotionBannerProps> = ({
  ContentPopperLayoutComponent,
  onClick,
  className,
  'data-testid': dataTestid,
  renderingPriority,
  pageType,
  disableOverrides,
}) => {
  const headerHighlightParams = useHeaderHighlightParams();
  const {disableHeaderHighlight, priorityOverride} = disableOverrides
    ? {disableHeaderHighlight: false, priorityOverride: undefined}
    : headerHighlightParams;
  const inferredPageType = useCurrentPageType();
  const categoryIdentifier = useCategoryIdentifier();
  const productId = useProductIdFromSlug();
  const placement = placementForPageType(
    pageType ?? inferredPageType,
    categoryIdentifier,
    productId,
  );

  if (disableHeaderHighlight || !placement) {
    return null;
  }

  return (
    <PromotionBannerWithPlacement
      placement={placement}
      priorityOverride={priorityOverride}
      ContentPopperLayoutComponent={ContentPopperLayoutComponent}
      onClick={onClick}
      className={className}
      data-testid={dataTestid}
      renderingPriority={renderingPriority}
    />
  );
};

interface PromotionBannerWithPlacementProps extends PromotionBannerProps {
  placement: GetBannerRequest_Placement;
  priorityOverride: GetBannerRequest_PriorityOverride | undefined;
}

const PromotionBannerWithPlacement: FC<PromotionBannerWithPlacementProps> = ({
  placement,
  priorityOverride,
  ContentPopperLayoutComponent,
  onClick,
  className,
  'data-testid': dataTestid,
  renderingPriority,
}) => (
  <DataFetcher
    request={useBannerRequest(placement, priorityOverride)}
    View={PromotionBannerView}
    Loading={() => null}
    Error={() => null}
    renderingPriority={renderingPriority}
    viewProps={{
      ContentPopperLayoutComponent,
      onClick,
      className,
      'data-testid': dataTestid,
    }}
  />
);
